import { Ref, useRef, useState } from 'react';
import { Check, CheckCircle, X } from 'lucide-react';
import { Button } from '@curtin-dxp/curtin-ui';
import { trpc } from '@/data-loaders';
import { cn } from '@/lib/utils';
import { useAwmsStore } from '@/store';
import { useElementSize, useHandleError, useHandleSuccess, useSchoolUnitList, useUnitStudyPeriodList } from '@/hooks';
import { IconComponent, ChevronIcon } from '@/components/Common';
import { SelectDropDown } from '@/components/Common/SelectDropDown/SelectDropDown';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface AddUnitBarProps {
  isVisible: boolean;
  isVisibleCallback: CallableFunction;
  parentRef: Ref<HTMLDivElement>;
  schoolList: { id: number; name: string }[] | undefined;
}

export const AddUnitBar = (props: AddUnitBarProps) => {
  const sendToastStaffUnit = useHandleSuccess({
    title: 'Add staff unit',
  });
  const handleError = useHandleError();
  const trpcUtils = trpc.useUtils();
  const globalSelectedSchoolId = useAwmsStore((state) => state.selectedSchoolId);
  const globalSelectedSchool = props.schoolList?.filter((school) => school.id == globalSelectedSchoolId)[0];
  const selectedStaffId = useAwmsStore((state) => state.selectedStaff.id);
  const [triggerRef, { width: triggerWidth }] = useElementSize<HTMLButtonElement>();

  const [open, setOpen] = useState<boolean>(false);
  const [commandInputValue, setCommandInputValue] = useState<string>('');

  const [selectedSchool, setSelectedSchool] = useState<
    | {
        id: number;
        name: string;
      }
    | undefined
  >(globalSelectedSchool);

  const [selectedUnit, setSelectedUnit] = useState<
    | {
        id: number;
        name: string;
      }
    | undefined
  >(undefined);
  const { data: unitList } = useSchoolUnitList(selectedSchool?.id ?? globalSelectedSchoolId);

  const [selectedStudyPeriod, setSelectedStudyPeriod] = useState<
    | {
        id: number;
        name: string;
      }
    | undefined
  >(undefined);
  const { data: studyPeriodList } = useUnitStudyPeriodList(selectedUnit?.id ?? 0);

  const handleInvalidateAndRefetch = async () => {
    await trpcUtils.unit.getStaffUnitDetails.invalidate({ staffId: undefined });
    await trpcUtils.unit.getStaffUnitDetails.refetch();
  };

  const addUnitMutation = trpc.unit.addUnitWorkloadAcademicByUnitAndStudyPeriod.useMutation({
    onSuccess: () => {
      handleInvalidateAndRefetch();
      sendToastStaffUnit();
    },
    onError: (error) => {
      console.error('Error adding a unit to academic:', error);
      handleError([error]);
    },
  });

  const selectSchool = (id: number): void => {
    setSelectedSchool(props.schoolList?.filter((school) => school.id == id)[0]);
    setSelectedUnit(undefined);
    setSelectedStudyPeriod(undefined);
  };

  const selectUnit = (id: number): void => {
    setSelectedUnit(unitList?.filter((unit) => unit.id === id)[0]);
    setSelectedStudyPeriod(undefined);
  };

  const selectStudyPeriod = (id: number): void => {
    setSelectedStudyPeriod(studyPeriodList?.filter((studyPeriod) => studyPeriod.id === id)[0]);
  };

  const resetSelections = (): void => {
    setSelectedSchool(globalSelectedSchool);
    setSelectedUnit(undefined);
    setSelectedUnit(undefined);
  };

  const formRef = useRef<HTMLDivElement>(null);

  const handleConfirm = (): void => {
    const children = formRef.current?.childNodes;
    if (!children) return;

    let requiredMissing: boolean = false;
    children.forEach((child) => {
      if (child.nodeType !== 1) return;
      const element = child as Element;
      if (element.getAttribute('role') !== 'combobox' || element.getAttribute('aria-required') !== 'true') return;
      if ('title' in element && (element.title as string).length == 0) {
        temporaryInvalid(element);
        requiredMissing = true;
      }
    });

    if (requiredMissing) return;

    // Add the unit
    if (selectedStaffId && selectedUnit && selectedStudyPeriod)
      addUnitMutation.mutate({
        academicId: selectedStaffId,
        unitId: selectedUnit.id,
        studyPeriodId: selectedStudyPeriod.id,
      });

    // Dismiss the add unit bar
    if (typeof props.isVisibleCallback == 'function') props.isVisibleCallback();
  };

  const temporaryInvalid = async (element: Element): Promise<void> => {
    element.ariaInvalid = 'true';
    setTimeout(() => (element.ariaInvalid = null), 1000);
  };

  const handleCancel = (): void => {
    if (typeof props.isVisibleCallback == 'function') props.isVisibleCallback();
    resetSelections();
  };

  return (
    // absolute -left-[1px] top-[88px] z-50 h-[95px] w-[calc(100%_+_1px)]
    <div
      ref={formRef}
      className={`${props.isVisible ? 'visible' : 'hidden'} mt-0 flex h-[95px] w-full items-center gap-5 self-stretch bg-primary-black-90
        px-5`}
    >
      <SelectDropDown
        className="dropdown-school w-[356px]"
        list={props.schoolList}
        valueChangeCallback={selectSchool}
        initialValue={globalSelectedSchoolId}
        required={true}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild ref={triggerRef}>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={`h-[38px] w-[240px] justify-between rounded-lg border-[1px] border-input bg-white px-3 py-5 text-b2 font-normal
              text-secondary-grey-01`}
            disabled={unitList?.length === 0}
          >
            <span className="truncate pr-1">
              {commandInputValue ? unitList?.find((unit) => unit.name === commandInputValue)?.name : 'Select a unit'}
            </span>
            <ChevronIcon
              className="h-[13px] w-[13px] fill-[#000000] transition-transform duration-300"
              style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0" align="start" style={{ width: `${triggerWidth + 100}px` }}>
          <Command className="bg-white">
            <CommandInput
              placeholder="Search unit..."
              aria-label="Search unit"
              className="h-[38px] w-[447px] bg-white px-3 pl-0 text-b2 font-normal placeholder:text-secondary-grey-01 hover:border-deep-blue
                focus-visible:ring-deep-blue"
            />
            <CommandList>
              <CommandEmpty className="p-5 text-base text-secondary-grey-01">No unit found.</CommandEmpty>
              <CommandGroup>
                {unitList?.map((unit) => (
                  <CommandItem
                    key={unit.id}
                    value={unit.name}
                    onSelect={(currentValue) => {
                      selectUnit(unit.id);
                      setCommandInputValue(currentValue);
                      setOpen(false);
                    }}
                    className="disabled py-[10px] text-b3 focus:text-deep-blue data-[selected=true]:text-deep-blue"
                  >
                    <span className="max-w-full flex-1 truncate">{unit.name}</span>
                    <Check
                      className={cn('mr-2 h-4 w-4', commandInputValue === unit.name ? 'opacity-100' : 'opacity-0')}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <SelectDropDown
        className="dropdown-studyperiod w-[242px]"
        list={studyPeriodList}
        valueChangeCallback={selectStudyPeriod}
        initialValue={selectedUnit?.id}
        placeholder="Select a study period"
        required={true}
      />
      <Button
        onClick={() => {
          handleConfirm();
        }}
        variant="filled-tertiary"
        className="flex h-10 flex-shrink-0 flex-grow-0 flex-row justify-center gap-[10px] rounded-lg px-[30px] py-[15px] text-b2
          font-semibold hover:text-tertiary"
      >
        Confirm <IconComponent IconType={CheckCircle} className="size-6" />
      </Button>
      <Button
        onClick={() => {
          handleCancel();
        }}
        variant="outline-secondary"
        className="flex h-10 flex-shrink-0 flex-grow-0 flex-row justify-center gap-[10px] rounded-lg border-white px-[30px] py-[15px]
          text-b2 font-semibold text-white hover:bg-white hover:text-black"
      >
        Cancel <IconComponent IconType={X} className="size-6" />
      </Button>
    </div>
  );
};
